import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, Loader2 } from "lucide-react";
import { getDeletedEntries, restoreEntry, purgeEntry, purgeAllDeleted } from "../lib/api";

function relTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const pretty = (v) => (v ? String(v).toLowerCase().replace(/_/g, " ") : null);

function deletedContext(e) {
  const when = relTime(e.deleted_at || e.updated_at);
  const bits = [];
  if (when) bits.push(`Deleted ${when}`);
  const type = pretty(e.type);
  if (type) bits.push(type);
  const area = pretty(e.domain);
  if (area && area !== "personal") bits.push(area);
  return bits.join(" · ");
}

// Recently deleted: soft-deleted items remain restorable here until permanently removed.
// No automatic retention/cleanup job exists — items stay until the user acts. Copy is honest.
export function RecentlyDeletedCard() {
  const [items, setItems] = useState(null); // null = loading
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null); // item awaiting "delete forever" confirm
  const [confirmAll, setConfirmAll] = useState(false);
  const [purgingAll, setPurgingAll] = useState(false);

  useEffect(() => {
    getDeletedEntries().then(setItems).catch(() => setItems([]));
  }, []);

  const restore = (id) => {
    setBusyId(id);
    restoreEntry(id)
      .then(() => { setItems((l) => l.filter((e) => e.id !== id)); toast.success("Restored."); })
      .catch(() => toast.error("Couldn’t restore that. Please try again."))
      .finally(() => setBusyId(null));
  };

  const purge = (id) => {
    setBusyId(id);
    purgeEntry(id)
      .then((r) => {
        if (!r.purged) throw new Error("not purged");
        setItems((l) => l.filter((e) => e.id !== id));
        setConfirmId(null);
        toast.success("Permanently deleted.");
      })
      .catch(() => toast.error("Couldn’t permanently delete that. Please try again."))
      .finally(() => setBusyId(null));
  };

  const purgeAll = () => {
    setPurgingAll(true);
    purgeAllDeleted()
      .then((r) => {
        setItems([]);
        setConfirmAll(false);
        toast.success(`Permanently deleted ${r.purged} item${r.purged === 1 ? "" : "s"}.`);
      })
      .catch(() => toast.error("Couldn’t delete those. Nothing was removed. Please try again."))
      .finally(() => setPurgingAll(false));
  };

  if (items === null) {
    return (
      <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 md:p-7 mb-6 flex items-center gap-2 text-[var(--ink4)]"
        data-testid="recently-deleted-loading">
        <Loader2 size={15} className="animate-spin" /> <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (items.length === 0) return null;

  return (
    <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 md:p-7 mb-6"
      data-testid="recently-deleted-card">
      <div className="flex items-center gap-2 mb-2">
        <Trash2 size={16} className="text-[var(--ink3)]" strokeWidth={1.9} />
        <h2 className="text-sm uppercase tracking-[0.18em] font-bold text-[var(--ink3)]">Recently deleted</h2>
      </div>
      <p className="text-sm text-[var(--ink3)] mb-4">
        Deleted items stay here until you restore them or delete them for good. Nothing is removed automatically.
      </p>

      {!confirmAll ? (
        <button
          data-testid="purge-all-btn"
          onClick={() => setConfirmAll(true)}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--danger)] hover:bg-[var(--danger-soft)] rounded-full px-3 py-1.5 transition-colors duration-200"
        >
          <Trash2 size={14} strokeWidth={2} /> Delete all forever
        </button>
      ) : (
        <div className="mb-4 rounded-xl border border-[var(--danger-soft)] bg-[var(--danger-soft)] px-3.5 py-3" data-testid="purge-all-confirm">
          <p className="text-[13px] text-[var(--danger)] mb-2.5">Permanently delete all recently deleted items? This cannot be undone.</p>
          <div className="flex items-center gap-2">
            <button
              data-testid="purge-all-cancel-btn"
              onClick={() => setConfirmAll(false)}
              disabled={purgingAll}
              className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] disabled:opacity-50 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              data-testid="purge-all-confirm-btn"
              onClick={purgeAll}
              disabled={purgingAll}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--danger)] hover:bg-[var(--danger-dark)] text-[var(--on-pine)] px-4 py-2 text-sm font-semibold disabled:opacity-50 transition-colors duration-200"
            >
              {purgingAll ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={2} />}
              Delete all forever
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((e) => {
          const confirming = confirmId === e.id;
          const busy = busyId === e.id;
          return (
            <div key={e.id} data-testid={`deleted-item-${e.id}`}
              className="rounded-xl border border-[var(--line2)] bg-[var(--surface3)] px-3.5 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[var(--ink)] truncate">
                    {e.interpreted_summary || e.raw_text || "Item"}
                  </p>
                  <p className="text-xs text-[var(--ink5)] mt-0.5 truncate" data-testid={`deleted-context-${e.id}`}>
                    {deletedContext(e)}
                  </p>
                </div>
                {!confirming && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`restore-deleted-btn-${e.id}`}
                      onClick={() => restore(e.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] hover:text-[var(--pine)] disabled:opacity-50 transition-all duration-200"
                    >
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} strokeWidth={2} />}
                      Restore
                    </button>
                    <button
                      data-testid={`purge-deleted-btn-${e.id}`}
                      onClick={() => setConfirmId(e.id)}
                      disabled={busy}
                      className="inline-flex items-center rounded-full px-3 py-2 text-sm font-semibold text-[var(--danger)] hover:bg-[var(--danger-soft)] disabled:opacity-50 transition-colors duration-200"
                    >
                      Delete forever
                    </button>
                  </div>
                )}
              </div>

              {confirming && (
                <div className="mt-2.5 flex items-center justify-between gap-3" data-testid={`purge-confirm-${e.id}`}>
                  <p className="text-[13px] text-[var(--danger)] min-w-0">Delete permanently? This can’t be undone.</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`purge-cancel-btn-${e.id}`}
                      onClick={() => setConfirmId(null)}
                      disabled={busy}
                      className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] disabled:opacity-50 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      data-testid={`purge-confirm-btn-${e.id}`}
                      onClick={() => purge(e.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--danger)] hover:bg-[var(--danger-dark)] text-[var(--on-pine)] px-4 py-2 text-sm font-semibold disabled:opacity-50 transition-colors duration-200"
                    >
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={2} />}
                      Delete forever
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RecentlyDeletedCard;
